import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Info, Upload, UserCircle2, Loader2, Mail, RefreshCcw, Eye, ChevronDown, ChevronRight, Copy, Calendar as CalendarIcon, ExternalLink, Download } from "lucide-react";
import EmailBodyEditor, { parseVariableTokens } from "@/components/admin/EmailBodyEditor";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function AdminProfileSection() {
  const [me, setMe] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/me").then((r) => setMe(r.data)).catch(() => {}); }, []);

  const onFile = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) return toast.error("PNG, JPEG ou WEBP uniquement");
    if (file.size > 1_500_000) return toast.error("Image trop volumineuse (max 1,5 Mo)");
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",", 2)[1] || "");
        r.onerror = rej; r.readAsDataURL(file);
      });
      const r = await api.post("/admin/me/avatar", { filename: file.name, mime_type: file.type, data_base64: b64 });
      setMe((m) => ({ ...m, avatar_url: r.data.avatar_url }));
      toast.success("Avatar mis à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/me", {
        name: me?.name || "",
        title: me?.title || "",
        mobile: me?.mobile || "",
        linkedin_url: me?.linkedin_url || "",
        instagram_url: me?.instagram_url || "",
        facebook_url: me?.facebook_url || "",
        twitter_url: me?.twitter_url || "",
        website_url: me?.website_url || "",
        signature_tagline: me?.signature_tagline || "",
      });
      setMe(r.data);
      toast.success("Profil enregistré");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  if (!me) return null;
  return (
    <Section title="Mon profil & signature email" subtitle="Avatar, fonction, coordonnées et réseaux sociaux : tout ceci apparaît automatiquement dans la signature de tous les emails envoyés (devis, factures, relances, RDV).">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative">
          {me.avatar_url ? (
            <img src={me.avatar_url} alt="avatar" className="w-20 h-20 rounded-full object-cover border-2 border-[#06B6D4]/40 shadow-md" data-testid="admin-avatar-img" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-[#FAFAFA] border-2 border-dashed border-black/15 flex items-center justify-center text-[#06B6D4]">
              <UserCircle2 size={44} />
            </div>
          )}
          <label className="absolute -bottom-1 -right-1 bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] rounded-full w-8 h-8 flex items-center justify-center shadow cursor-pointer transition-colors" data-testid="admin-avatar-upload">
            {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
              disabled={uploading}
            />
          </label>
        </div>
        <div className="flex-1 min-w-[200px] grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Nom affiché</Label><Input data-testid="admin-name" value={me.name || ""} onChange={(e) => setMe({ ...me, name: e.target.value })} placeholder="Ex : Hugo Besson" /></div>
          <div><Label>Fonction</Label><Input data-testid="admin-title" value={me.title || ""} onChange={(e) => setMe({ ...me, title: e.target.value })} placeholder="Ex : Fondateur" /></div>
          <div><Label>Mobile</Label><Input data-testid="admin-mobile" value={me.mobile || ""} onChange={(e) => setMe({ ...me, mobile: e.target.value })} placeholder="Ex : 06 12 34 56 78" /></div>
          <div className="sm:col-span-2"><Label>Email</Label><Input value={me.email || ""} disabled /></div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Label>Tagline / accroche signature</Label><Input data-testid="admin-tagline" value={me.signature_tagline || ""} onChange={(e) => setMe({ ...me, signature_tagline: e.target.value })} placeholder="Ex : Conseil & développement sur-mesure" /></div>
        <div><Label>LinkedIn</Label><Input data-testid="admin-linkedin" value={me.linkedin_url || ""} onChange={(e) => setMe({ ...me, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/..." /></div>
        <div><Label>Instagram</Label><Input data-testid="admin-instagram" value={me.instagram_url || ""} onChange={(e) => setMe({ ...me, instagram_url: e.target.value })} placeholder="https://instagram.com/..." /></div>
        <div><Label>Facebook</Label><Input data-testid="admin-facebook" value={me.facebook_url || ""} onChange={(e) => setMe({ ...me, facebook_url: e.target.value })} placeholder="https://facebook.com/..." /></div>
        <div><Label>X / Twitter</Label><Input data-testid="admin-twitter" value={me.twitter_url || ""} onChange={(e) => setMe({ ...me, twitter_url: e.target.value })} placeholder="https://x.com/..." /></div>
        <div className="sm:col-span-2"><Label>Site web personnel</Label><Input data-testid="admin-website" value={me.website_url || ""} onChange={(e) => setMe({ ...me, website_url: e.target.value })} placeholder="https://votre-site.com" /></div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#0A1128] hover:bg-[#14213d] text-white" data-testid="admin-profile-save">
          {saving ? <><Loader2 className="animate-spin mr-2" size={14} />Enregistrement…</> : "Enregistrer mon profil"}
        </Button>
      </div>

      <EmailSignatureExport />
    </Section>
  );
}

function EmailSignatureExport() {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-signature/html`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("besson_admin_token")}` },
      });
      setHtml(await resp.text());
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copyHtml = () => {
    navigator.clipboard?.writeText(html);
    toast.success("Code HTML copié !");
  };

  const copyRendered = async () => {
    // Copy the rendered version (rich text + image) — works in Gmail/Outlook
    try {
      const blob = new Blob([html], { type: "text/html" });
      const data = [new ClipboardItem({ "text/html": blob, "text/plain": new Blob([html], { type: "text/plain" }) })];
      await navigator.clipboard.write(data);
      toast.success("Signature copiée (collez dans Gmail/Outlook avec Ctrl+V) ✉️");
    } catch {
      // Fallback: select & copy via execCommand
      copyHtml();
    }
  };

  const download = async () => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-signature/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("besson_admin_token")}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "besson-email-signature.html";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-black/8" data-testid="email-signature-export">
      <div className="flex items-start gap-3 mb-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="display font-bold text-[#0A1128] text-sm">Signature email — Export HTML</h3>
          <p className="text-xs text-[#64748B] mt-0.5">Réutilisez votre signature dans Gmail, Outlook, Apple Mail, Spark, etc. Elle reflète les infos ci-dessus en temps réel.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={copyRendered} disabled={loading} variant="outline" data-testid="sig-copy-rendered" className="text-xs">
            <Copy size={12} className="mr-1.5" /> Copier la signature
          </Button>
          <Button onClick={copyHtml} disabled={loading} variant="outline" data-testid="sig-copy-html" className="text-xs">
            <Copy size={12} className="mr-1.5" /> Copier le code HTML
          </Button>
          <Button onClick={download} disabled={loading} variant="outline" data-testid="sig-download" className="text-xs">
            <Download size={12} className="mr-1.5" /> Télécharger .html
          </Button>
        </div>
      </div>

      {/* Aperçu */}
      <div className="rounded-xl border border-black/10 bg-white p-3 mb-3" data-testid="sig-preview">
        {loading ? (
          <div className="text-xs text-[#64748B] text-center py-4"><Loader2 className="animate-spin inline mr-1" size={12} /> Chargement…</div>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </div>

      {/* Code source */}
      <details className="bg-[#FAFAFA] rounded-xl border border-black/8">
        <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-[#0A1128]">Voir le code HTML (à coller dans Gmail → Paramètres → Signature)</summary>
        <Textarea
          readOnly
          value={html}
          rows={10}
          className="font-mono text-[10px] bg-white border-0 rounded-b-xl resize-y"
          data-testid="sig-html-textarea"
          onClick={(e) => e.target.select()}
        />
      </details>

      <div className="bg-[#A7F3D0]/20 border border-[#A7F3D0] rounded-lg p-2.5 mt-3 text-[11px] text-[#065F46]">
        <b>Gmail :</b> Paramètres ⚙ → Voir tous les paramètres → onglet « Général » → Signature → Créer → coller avec <kbd className="bg-white px-1 rounded text-[9px]">Ctrl+V</kbd> (« Copier la signature »).<br />
        <b>Outlook :</b> Fichier → Options → Courrier → Signatures → Nouvelle → coller dans la zone de texte enrichi.<br />
        <b>Apple Mail :</b> Mail → Paramètres → Signatures → glisser le fichier .html téléchargé.
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [s, setS] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setS(r.data || {});
      if (r.data?.contact_email && !testEmail) setTestEmail(r.data.contact_email);
    }).catch(() => {});
    api.get("/admin/email/status").then((r) => setEmailStatus(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testSmtp = async () => {
    if (!testEmail) return;
    setTesting(true);
    setTestResult(null);
    try {
      // Persist current settings first so test uses the latest values
      const payload = { ...s, smtp_port: s.smtp_port ? parseInt(s.smtp_port) : 465 };
      await api.put("/admin/settings", payload);
      const r = await api.post("/admin/email/test", { to: testEmail, provider: "smtp" });
      setTestResult(r.data);
      // refresh status
      const st = await api.get("/admin/email/status");
      setEmailStatus(st.data);
      if (r.data.sent) toast.success("Email de test envoyé !");
      else toast.error("Échec du test — vérifiez vos identifiants");
    } catch (e) {
      setTestResult({ sent: false, provider_used: "smtp" });
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setTesting(false); }
  };

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        ...s,
        smtp_port: s.smtp_port ? parseInt(s.smtp_port) : 465,
        slot_duration_min: s.slot_duration_min ? parseInt(s.slot_duration_min) : 30,
      };
      await api.put("/admin/settings", payload);
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Échec"); } finally { setLoading(false); }
  };

  const addAvail = () => setS({ ...s, availability: [...(s.availability || []), { day_of_week: 1, start: "09:00", end: "12:00" }] });
  const updAvail = (i, p) => setS({ ...s, availability: (s.availability || []).map((a, k) => k === i ? { ...a, ...p } : a) });
  const delAvail = (i) => setS({ ...s, availability: (s.availability || []).filter((_, k) => k !== i) });

  return (
    <div data-testid="admin-settings" className="space-y-5">
      <div>
        <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Paramètres</h1>
        <p className="text-sm text-[#64748B] mt-1">Configurez SMTP, Google, infos entreprise (facturation) et disponibilités.</p>
      </div>

      <AdminProfileSection />

      <EmailTemplatesSection />

      <PublicVisibilitySection />

      {/* Coordonnées affichées */}
      <Section title="Coordonnées affichées sur le site">
        <Row><Label>Téléphone</Label><Input data-testid="setting-phone" value={s.contact_phone || ""} onChange={(e) => setS({ ...s, contact_phone: e.target.value })} placeholder="01 XX XX XX XX (fixe à venir)" /></Row>
        <Row><Label>Email</Label><Input data-testid="setting-email" value={s.contact_email || ""} onChange={(e) => setS({ ...s, contact_email: e.target.value })} /></Row>
        <Row><Label>URL publique du site <span className="text-xs text-[#64748B]">(utilisée pour les liens dans les emails)</span></Label>
          <Input data-testid="setting-public-url" placeholder="https://besson-solutions.fr" value={s.public_app_url || ""} onChange={(e) => setS({ ...s, public_app_url: e.target.value })} />
        </Row>
      </Section>

      {/* Vidéo d'accueil */}
      <Section title="Vidéo d'arrière-plan — Page d'accueil" subtitle="Collez un lien YouTube : la vidéo sera en autoplay, muet et en boucle derrière le hero. Laissez vide pour utiliser la vidéo de démo (équipe en bureau).">
        <Row>
          <Label>Lien YouTube</Label>
          <Input
            data-testid="setting-hero-video"
            placeholder="https://www.youtube.com/watch?v=XXXXXXXXXXX"
            value={s.hero_video_url || ""}
            onChange={(e) => setS({ ...s, hero_video_url: e.target.value })}
          />
          <p className="text-xs text-[#64748B] mt-1.5">Formats acceptés : <code className="bg-[#FAFAFA] px-1 py-0.5 rounded">youtube.com/watch?v=</code>, <code className="bg-[#FAFAFA] px-1 py-0.5 rounded">youtu.be/</code>, <code className="bg-[#FAFAFA] px-1 py-0.5 rounded">youtube.com/embed/</code></p>
        </Row>
      </Section>

      {/* Stats animées */}
      <Section title="Compteurs animés — Page d'accueil" subtitle="Ces chiffres s'animent quand la section devient visible (count-up).">
        <Row2>
          <div>
            <Label>Années d'expérience</Label>
            <Input
              data-testid="setting-years"
              type="number"
              min="0"
              max="99"
              value={s.years_experience ?? ""}
              onChange={(e) => setS({ ...s, years_experience: e.target.value === "" ? null : parseInt(e.target.value) })}
            />
          </div>
          <div>
            <Label>Nombre d'entreprises accompagnées</Label>
            <Input
              data-testid="setting-projects"
              type="number"
              min="0"
              value={s.projects_count ?? ""}
              onChange={(e) => setS({ ...s, projects_count: e.target.value === "" ? null : parseInt(e.target.value) })}
            />
          </div>
        </Row2>
      </Section>

      {/* Email provider status */}
      <Section title="Envoi d'emails — Statut du fournisseur" subtitle="2 options possibles : SMTP (votre hébergeur) OU Resend (recommandé, 3000 emails/mois gratuits). Une seule suffit.">
        {emailStatus ? (
          <div data-testid="email-status-card" className={`rounded-xl p-4 border ${emailStatus.provider === "none" ? "bg-[#FFDCA8]/30 border-[#FFDCA8] text-[#92400E]" : "bg-[#A7F3D0]/30 border-[#A7F3D0] text-[#065F46]"}`}>
            <div className="flex items-center gap-2 font-bold text-sm">
              {emailStatus.provider === "resend" && <>✅ Resend activé · expéditeur : <code className="bg-white px-1.5 py-0.5 rounded text-xs">{emailStatus.sender_email || "onboarding@resend.dev"}</code></>}
              {emailStatus.provider === "smtp" && <>✅ SMTP activé · expéditeur : <code className="bg-white px-1.5 py-0.5 rounded text-xs">{s.smtp_from || s.smtp_user}</code></>}
              {emailStatus.provider === "none" && <>⚠️ Aucun fournisseur email configuré · les emails ne partiront pas</>}
            </div>
            <div className="text-xs mt-2 opacity-80">
              {emailStatus.provider === "none" && "Configurez le bloc SMTP ci-dessous (le plus simple si vous avez déjà un email pro) OU une clé Resend dans /app/backend/.env."}
              {emailStatus.provider === "resend" && emailStatus.sender_email?.includes("onboarding@resend.dev") && "Pour envoyer depuis votre propre domaine, vérifiez-le sur resend.com/domains puis définissez SENDER_EMAIL dans le backend."}
              {emailStatus.provider === "smtp" && "SMTP opérationnel. Vous pouvez tester l'envoi dans le bloc SMTP ci-dessous."}
            </div>
          </div>
        ) : <div className="text-xs text-[#64748B]">Chargement…</div>}
      </Section>

      {/* Disponibilités */}
      <Section title="Disponibilités hebdomadaires" subtitle="Les créneaux générés s'afficheront automatiquement sur la page de prise de RDV.">
        <div className="space-y-2">
          {(s.availability || []).map((a, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 bg-[#FAFAFA] p-3 rounded-lg border border-black/8">
              <select value={a.day_of_week} onChange={(e) => updAvail(i, { day_of_week: parseInt(e.target.value) })} className="border border-black/10 rounded-md px-2 py-1.5 text-sm">
                {DAYS.map((d, k) => <option key={k} value={k}>{d}</option>)}
              </select>
              <Input className="w-28" type="time" value={a.start} onChange={(e) => updAvail(i, { start: e.target.value })} />
              <span className="text-[#64748B]">→</span>
              <Input className="w-28" type="time" value={a.end} onChange={(e) => updAvail(i, { end: e.target.value })} />
              <button onClick={() => delAvail(i)} className="ml-auto text-[#94a3b8] hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
          <Button onClick={addAvail} variant="outline" size="sm"><Plus size={14} className="mr-1" />Ajouter une plage</Button>
        </div>
      </Section>

      {/* SMTP */}
      <Section title="SMTP — Envoi d'emails (port 465 SSL)" subtitle="Pour les emails de confirmation RDV, devis envoyés et signatures. À configurer chez votre hébergeur.">
        <div className="bg-[#FFDCA8]/30 border border-[#FFDCA8] rounded-lg p-3 text-xs text-[#92400E] flex items-start gap-2 mb-3">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <b>Configuration courante :</b><br />
            • OVH : <code className="bg-white px-1 py-0.5 rounded">ssl0.ovh.net</code> · port <code className="bg-white px-1 py-0.5 rounded">465</code><br />
            • Gandi : <code className="bg-white px-1 py-0.5 rounded">mail.gandi.net</code> · port <code className="bg-white px-1 py-0.5 rounded">465</code><br />
            • Google Workspace : <code className="bg-white px-1 py-0.5 rounded">smtp.gmail.com</code> · port <code className="bg-white px-1 py-0.5 rounded">465</code> (utilisez un <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-bold">App Password</a>)<br />
            • IONOS : <code className="bg-white px-1 py-0.5 rounded">smtp.ionos.fr</code> · port <code className="bg-white px-1 py-0.5 rounded">465</code><br />
            Renseignez l'expéditeur identique à l'identifiant pour éviter les rejets SPF/DKIM.
          </div>
        </div>
        <Row2>
          <div><Label>Hôte SMTP</Label><Input data-testid="setting-smtp-host" placeholder="ssl0.ovh.net" value={s.smtp_host || ""} onChange={(e) => setS({ ...s, smtp_host: e.target.value })} /></div>
          <div><Label>Port</Label><Input data-testid="setting-smtp-port" type="number" placeholder="465" value={s.smtp_port || 465} onChange={(e) => setS({ ...s, smtp_port: e.target.value })} /></div>
        </Row2>
        <Row2>
          <div><Label>Identifiant</Label><Input data-testid="setting-smtp-user" placeholder="contact@besson-solutions.fr" value={s.smtp_user || ""} onChange={(e) => setS({ ...s, smtp_user: e.target.value })} /></div>
          <div><Label>Mot de passe</Label><Input data-testid="setting-smtp-password" type="password" value={s.smtp_password || ""} onChange={(e) => setS({ ...s, smtp_password: e.target.value })} /></div>
        </Row2>
        <Row2>
          <div><Label>Email expéditeur</Label><Input data-testid="setting-smtp-from" placeholder="contact@besson-solutions.fr" value={s.smtp_from || ""} onChange={(e) => setS({ ...s, smtp_from: e.target.value })} /></div>
          <div><Label>Nom expéditeur</Label><Input data-testid="setting-smtp-from-name" placeholder="BESSON SOLUTIONS" value={s.smtp_from_name || ""} onChange={(e) => setS({ ...s, smtp_from_name: e.target.value })} /></div>
        </Row2>
        <Row>
          <Label>Provider à utiliser</Label>
          <select data-testid="setting-email-provider" className="w-full h-9 border border-input rounded-md px-2 text-sm" value={s.email_provider || "auto"} onChange={(e) => setS({ ...s, email_provider: e.target.value })}>
            <option value="auto">Auto (Resend si dispo, sinon SMTP)</option>
            <option value="smtp">Forcer SMTP (recommandé pour votre cas)</option>
            <option value="resend">Forcer Resend</option>
          </select>
          <p className="text-[11px] text-[#64748B] mt-1">Choisissez "Forcer SMTP" pour utiliser uniquement votre serveur SMTP (port 465 SSL).</p>
        </Row>

        {/* Test SMTP */}
        <div className="bg-[#FAFAFA] border border-black/8 rounded-lg p-3 mt-2">
          <Label className="text-xs">Tester l'envoi <span className="text-[#64748B]">(envoyez un email à vous-même pour valider la config)</span></Label>
          <div className="flex flex-col sm:flex-row gap-2 mt-1">
            <Input data-testid="setting-smtp-test-to" type="email" placeholder="votre.adresse@exemple.fr" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
            <Button data-testid="setting-smtp-test-btn" onClick={testSmtp} disabled={testing || !testEmail} className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] whitespace-nowrap">
              {testing ? "Envoi…" : "Envoyer un test"}
            </Button>
          </div>
          {testResult && (
            <div data-testid="setting-smtp-test-result" className={`mt-2 text-xs rounded-lg p-2 ${testResult.sent ? "bg-[#A7F3D0] text-[#065F46]" : "bg-[#FFDCA8] text-[#92400E]"}`}>
              {testResult.sent
                ? `✅ Email envoyé via ${testResult.provider_used?.toUpperCase()} ! Vérifiez votre boîte (+ spams).`
                : `⚠️ Échec : ${testResult.provider_used === "none" ? "aucun fournisseur configuré." : "vérifiez identifiant/mot de passe/hôte/port. Consultez les logs backend."}`}
            </div>
          )}
        </div>
      </Section>

      {/* Stripe (paiements CB) */}
      <Section title="Stripe — Paiements par carte bancaire" subtitle="Branchez vos clés (test ou production). Sans ces clés, les liens Stripe utilisent celles du serveur. Vous obtenez vos clés sur dashboard.stripe.com → Développeurs → Clés API.">
        <div className="bg-[#FFDCA8]/30 border border-[#FFDCA8] rounded-lg p-3 text-xs text-[#92400E] flex items-start gap-2 mb-3">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <b>Mode test</b> : clés <code className="bg-white px-1 py-0.5 rounded">pk_test_…</code> / <code className="bg-white px-1 py-0.5 rounded">sk_test_…</code><br />
            <b>Mode production</b> : clés <code className="bg-white px-1 py-0.5 rounded">pk_live_…</code> / <code className="bg-white px-1 py-0.5 rounded">sk_live_…</code><br />
            La clé secrète ne quitte jamais le serveur. Stockage chiffré au repos via MongoDB.
          </div>
        </div>

        {/* Master switch */}
        <div className={`flex items-start gap-3 rounded-xl p-3 mb-3 border ${s.stripe_enabled ? "bg-[#A7F3D0]/20 border-[#A7F3D0]" : "bg-[#FAFAFA] border-black/10"}`}>
          <input
            id="stripe_enabled"
            type="checkbox"
            data-testid="stripe-enabled-toggle"
            checked={!!s.stripe_enabled}
            onChange={(e) => setS({ ...s, stripe_enabled: e.target.checked })}
            className="mt-1 w-4 h-4 cursor-pointer"
          />
          <label htmlFor="stripe_enabled" className="flex-1 cursor-pointer">
            <div className="text-sm font-bold text-[#0A1128]">
              {s.stripe_enabled ? "✅ Paiement par carte ACTIVÉ" : "⛔ Paiement par carte DÉSACTIVÉ"}
            </div>
            <div className="text-xs text-[#64748B] mt-0.5">
              Master switch global. Désactivé = aucun bouton Stripe ne s'affiche (devis publics, factures, RDV) et les endpoints `/checkout/*` répondent 503. Vos clés restent enregistrées pour activation ultérieure.
            </div>
          </label>
        </div>

        <Row><Label>Clé publique (publishable key)</Label>
          <Input
            data-testid="setting-stripe-pub"
            placeholder="pk_live_… ou pk_test_…"
            value={s.stripe_publishable_key || ""}
            onChange={(e) => setS({ ...s, stripe_publishable_key: e.target.value })}
          />
        </Row>
        <Row><Label>Clé secrète (secret key)</Label>
          <Input
            data-testid="setting-stripe-secret"
            type="password"
            placeholder="sk_live_… ou sk_test_…"
            value={s.stripe_secret_key || ""}
            onChange={(e) => setS({ ...s, stripe_secret_key: e.target.value })}
          />
        </Row>
        <Row><Label>Webhook signing secret (optionnel)</Label>
          <Input
            data-testid="setting-stripe-webhook"
            type="password"
            placeholder="whsec_…"
            value={s.stripe_webhook_secret || ""}
            onChange={(e) => setS({ ...s, stripe_webhook_secret: e.target.value })}
          />
          <p className="text-xs text-[#64748B] mt-1.5">À configurer dans Stripe Dashboard → Développeurs → Webhooks. URL : <code className="bg-[#FAFAFA] px-1 py-0.5 rounded text-[10px] break-all">{(s.public_app_url || window.location.origin) + "/api/webhook/stripe"}</code></p>
        </Row>
      </Section>

      {/* iCal feed — simple alternative (recommended) */}
      <Section title="📅 Synchroniser vos RDV — Méthode simple (recommandée)" subtitle="Sans OAuth, sans Google Cloud : copiez l'URL ci-dessous et ajoutez-la à Google Calendar / Apple Calendar / Outlook. Vos RDV apparaîtront automatiquement et se rafraîchissent toutes les heures.">
        <ICalFeedBlock />
      </Section>

      {/* Google */}
      <Section title="Google Calendar — Méthode avancée (OAuth, optionnel)" subtitle="Synchronisation bidirectionnelle (lecture des événements Google + création directe via API). Nécessite la création d'un projet Google Cloud et d'identifiants OAuth.">
        <GoogleCalendarBlock s={s} setS={setS} />
      </Section>

      {/* Company info */}
      <Section title="Informations entreprise (facturation)" subtitle="Apparaissent sur les devis et factures PDF.">
        <Row><Label>Raison sociale</Label><Input data-testid="setting-legal-name" placeholder="BESSON SOLUTIONS — EURL" value={s.legal_name || ""} onChange={(e) => setS({ ...s, legal_name: e.target.value })} /></Row>
        <Row2>
          <div><Label>SIRET</Label><Input data-testid="setting-siret" value={s.siret || ""} onChange={(e) => setS({ ...s, siret: e.target.value })} /></div>
          <div><Label>N° TVA</Label><Input data-testid="setting-tva" value={s.vat_number || ""} onChange={(e) => setS({ ...s, vat_number: e.target.value })} /></div>
        </Row2>
        <Row><Label>Adresse de facturation</Label><Textarea rows={2} value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} /></Row>
        <Row2>
          <div><Label>IBAN</Label><Input data-testid="setting-iban" value={s.iban || ""} onChange={(e) => setS({ ...s, iban: e.target.value })} /></div>
          <div><Label>BIC</Label><Input data-testid="setting-bic" value={s.bic || ""} onChange={(e) => setS({ ...s, bic: e.target.value })} /></div>
        </Row2>
        <Row><Label>Titulaire du compte</Label><Input value={s.bank_holder || ""} onChange={(e) => setS({ ...s, bank_holder: e.target.value })} /></Row>
      </Section>

      {/* Chat widget */}
      <Section title="Chat externe & avis Google">
        <Row><Label>Widget chat flottant (HTML brut)</Label><Textarea data-testid="setting-chat-html" rows={5} placeholder="Collez ici le script Crisp, Tawk.to, Intercom..." value={s.chat_widget_html || ""} onChange={(e) => setS({ ...s, chat_widget_html: e.target.value })} /></Row>
        <Row>
          <Label>Google Place ID</Label>
          <Input value={s.google_place_id || ""} onChange={(e) => setS({ ...s, google_place_id: e.target.value })} placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4" data-testid="settings-google-place-id" />
          <p className="text-[11px] text-[#64748B] mt-1 leading-relaxed">
            🔍 Trouvez votre Place ID : <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noreferrer" className="text-[#06B6D4] hover:underline">Place ID Finder</a> — tapez "BESSON SOLUTIONS Istres" puis copiez l'ID. L'import automatique des avis Google sera activé prochainement (à venir).
          </p>
        </Row>
      </Section>

      <div className="sticky bottom-4 bg-white border border-black/8 rounded-2xl p-3 shadow-lg flex justify-end">
        <Button onClick={save} disabled={loading} data-testid="setting-save" className="bg-[#0A1128] hover:bg-[#14213d] text-white">
          {loading ? "Enregistrement..." : "Enregistrer tous les paramètres"}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-black/8 rounded-2xl p-5">
      <div className="mb-4">
        <h2 className="display font-bold text-[#0A1128]">{title}</h2>
        {subtitle && <p className="text-xs text-[#64748B] mt-0.5">{subtitle}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row({ children }) { return <div>{children}</div>; }
function Row2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }


function ICalFeedBlock() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/ical/info").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("URL copiée !");
  };

  const rotate = async () => {
    if (!window.confirm("Régénérer un nouveau lien ? L'ancien deviendra invalide. Vous devrez ré-ajouter le nouveau lien dans Google Calendar.")) return;
    setBusy(true);
    try {
      await api.post("/admin/ical/rotate");
      await load();
      toast.success("Nouvelle URL générée");
    } catch (e) {
      toast.error("Erreur lors de la régénération");
    } finally { setBusy(false); }
  };

  if (!info) return <div className="text-sm text-[#64748B]">Chargement…</div>;

  return (
    <div className="space-y-3">
      <div className="bg-[#A7F3D0]/20 border border-[#A7F3D0] rounded-lg p-3 text-xs text-[#065F46] flex items-start gap-2">
        <Info size={14} className="mt-0.5 flex-shrink-0" />
        <div>
          <b>3 étapes pour synchroniser avec Google Calendar :</b>
          <ol className="list-decimal pl-4 mt-1 space-y-0.5">
            <li>Copiez l'URL ci-dessous.</li>
            <li>Ouvrez <a className="underline font-semibold" href="https://calendar.google.com" target="_blank" rel="noreferrer">calendar.google.com</a> avec votre compte (ex : <code className="bg-white px-1 py-0.5 rounded text-[10px]">jbesson13800@gmail.com</code>).</li>
            <li>Dans le menu de gauche, cliquez sur <b>« + »</b> à côté de <b>« Autres agendas »</b> → <b>« À partir de l'URL »</b> → collez l'URL → <b>Ajouter l'agenda</b>.</li>
          </ol>
          <p className="mt-1.5 text-[#047857]">Vos RDV confirmés (et proposés en « tentative ») apparaîtront sous quelques minutes. Google rafraîchit toutes les heures environ.</p>
        </div>
      </div>

      <div>
        <Label className="text-xs">URL d'abonnement iCal (à copier dans Google Calendar)</Label>
        <div className="flex gap-2 items-stretch mt-1">
          <Input
            data-testid="ical-feed-url"
            value={info.url || ""}
            readOnly
            onClick={(e) => e.target.select()}
            className="font-mono text-xs"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => copy(info.url)}
            data-testid="ical-copy-btn"
            className="flex-shrink-0"
            title="Copier l'URL"
          >
            <Copy size={14} className="mr-1" /> Copier
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <a
          href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#0A1128] text-white hover:bg-[#14213d] transition-colors"
          data-testid="ical-open-google"
        >
          <CalendarIcon size={13} /> Ouvrir Google Calendar
          <ExternalLink size={12} />
        </a>
        <a
          href={info.webcal_url}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#06B6D4] text-[#06B6D4] hover:bg-[#06B6D4]/10 transition-colors"
          data-testid="ical-webcal-link"
          title="S'abonner sur macOS / iOS (Apple Calendar)"
        >
          <CalendarIcon size={13} /> Abonner via Apple Calendar
        </a>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={rotate}
          disabled={busy}
          className="text-xs ml-auto text-[#B91C1C] border-red-200 hover:bg-red-50"
          data-testid="ical-rotate-btn"
        >
          {busy ? <Loader2 className="animate-spin mr-1" size={12} /> : <RefreshCcw size={12} className="mr-1" />}
          Régénérer le lien
        </Button>
      </div>

      <p className="text-[11px] text-[#64748B] leading-relaxed">
        <b>Confidentialité :</b> l'URL contient un jeton secret. Ne la partagez pas publiquement. Si elle fuit, cliquez sur « Régénérer le lien » pour invalider l'ancienne.
      </p>
    </div>
  );
}

function GoogleCalendarBlock({ s, setS }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/admin/google/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/google/auth-url");
      window.open(r.data.auth_url, "google_oauth", "width=560,height=720");
      // Poll status every 2s for 60s to detect connection
      const start = Date.now();
      const t = setInterval(async () => {
        try { const st = await api.get("/admin/google/status"); if (st.data.connected) { clearInterval(t); setStatus(st.data); toast.success(`Connecté à ${st.data.email}`); } } catch {}
        if (Date.now() - start > 60000) clearInterval(t);
      }, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Déconnecter Google Calendar ? Les RDV existants resteront dans votre agenda Google.")) return;
    await api.post("/admin/google/disconnect"); toast.success("Déconnecté"); loadStatus();
  };
  const syncNow = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/google/sync-now"); toast.success(`Sync : ${r.data.pushed} RDV poussé(s) · ${r.data.failed} échec(s)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const callback = `${window.location.origin}/api/google/callback`;

  return (
    <div className="space-y-3">
      <div className="bg-[#FFDCA8]/30 border border-[#FFDCA8] rounded-lg p-3 text-xs text-[#92400E] flex items-start gap-2">
        <Info size={14} className="mt-0.5 flex-shrink-0" />
        <div>
          <b>Comment obtenir vos identifiants :</b>
          <ol className="list-decimal pl-4 mt-1 space-y-0.5">
            <li>Rendez-vous sur <a className="underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">console.cloud.google.com</a></li>
            <li>Créez un projet, puis activez l'API <b>Google Calendar</b> dans <b>APIs & Services → Library</b>.</li>
            <li>Dans <b>Credentials</b>, créez un <b>OAuth Client ID</b> de type <b>Web application</b>.</li>
            <li>Ajoutez l'URL de callback : <code className="bg-white px-1 py-0.5 rounded text-[10px] break-all">{callback}</code></li>
            <li>Copiez le Client ID et le Client Secret ci-dessous, sauvegardez puis cliquez sur <b>Connecter</b>.</li>
          </ol>
        </div>
      </div>
      <Row><Label>Google Client ID</Label><Input data-testid="setting-google-id" value={s.google_client_id || ""} onChange={(e) => setS({ ...s, google_client_id: e.target.value })} /></Row>
      <Row><Label>Google Client Secret</Label><Input data-testid="setting-google-secret" type="password" value={s.google_client_secret || ""} onChange={(e) => setS({ ...s, google_client_secret: e.target.value })} /></Row>
      <Row><Label>ID de l'agenda (primary par défaut)</Label><Input value={s.google_calendar_id || "primary"} onChange={(e) => setS({ ...s, google_calendar_id: e.target.value })} /></Row>

      {status && (
        <div className={`mt-2 rounded-xl p-3 border ${status.connected ? "bg-[#A7F3D0]/30 border-[#A7F3D0]" : "bg-[#FAFAFA] border-black/10"}`} data-testid="google-status">
          {status.connected ? (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-[#065F46]"><b>✓ Connecté</b> à <b>{status.email}</b> · Agenda : <code className="bg-white px-1 rounded">{status.calendar_id}</code></div>
              <div className="flex gap-2">
                <Button onClick={syncNow} disabled={busy} variant="outline" size="sm" data-testid="google-sync-now">Synchroniser maintenant</Button>
                <Button onClick={disconnect} variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="google-disconnect">Déconnecter</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-[#64748B]">Non connecté. Renseignez Client ID + Secret puis cliquez sur Connecter.</div>
              <Button onClick={connect} disabled={busy || !status.configured} className="bg-[#0A1128] hover:bg-[#14213d] text-white" data-testid="google-connect">
                {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : null}Connecter Google Calendar
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SubjectField({ slug, value, onChange, placeholder, variables = [] }) {
  const inputRef = useRef(null);
  const insertVar = (v) => {
    const el = inputRef.current;
    if (!el) { onChange((value || "") + v); return; }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = (value || "").slice(0, start) + v + (value || "").slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + v.length;
      el.setSelectionRange(pos, pos);
    });
  };
  return (
    <div>
      <Label className="text-xs">Sujet de l'email</Label>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`email-tpl-subject-${slug}`}
        placeholder={placeholder}
      />
      {variables.length > 0 && (
        <div className="flex items-center flex-wrap gap-1 mt-1.5">
          <span className="text-[10px] uppercase font-bold tracking-widest text-[#64748B] mr-1">Variables :</span>
          {variables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertVar(v)}
              data-testid={`email-subj-var-${slug}-${v.replace(/[{}]/g, "")}`}
              className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-white border border-[#06B6D4]/30 text-[#06B6D4] hover:bg-[#06B6D4] hover:text-white transition-colors"
            >
              {v}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [openSlug, setOpenSlug] = useState(null);
  const [savingSlug, setSavingSlug] = useState(null);
  const [preview, setPreview] = useState(null); // { slug, subject, html }
  const [draft, setDraft] = useState({}); // slug -> {subject, intro_html, cta_label}

  const load = async () => {
    try {
      const r = await api.get("/admin/email-templates");
      setTemplates(r.data);
      // Initialize drafts from current values
      const d = {};
      r.data.forEach((t) => { d[t.slug] = { subject: t.subject, intro_html: t.intro_html, cta_label: t.cta_label || "" }; });
      setDraft(d);
    } catch { toast.error("Erreur de chargement des templates"); }
  };
  useEffect(() => { load(); }, []);

  const setField = (slug, key, value) => setDraft((d) => ({ ...d, [slug]: { ...d[slug], [key]: value } }));

  const save = async (slug) => {
    const t = templates.find((x) => x.slug === slug);
    if (!t) return;
    const cur = draft[slug] || {};
    // Only persist values that differ from default (empty string = revert to default)
    const payload = {};
    if (cur.subject !== t.default_subject) payload.subject = cur.subject;
    if (cur.intro_html !== t.default_intro_html) payload.intro_html = cur.intro_html;
    if (cur.cta_label !== t.default_cta_label) payload.cta_label = cur.cta_label;
    setSavingSlug(slug);
    try {
      await api.put(`/admin/email-templates/${slug}`, payload);
      toast.success("Template enregistré");
      await load();
    } catch { toast.error("Erreur"); }
    finally { setSavingSlug(null); }
  };

  const reset = async (slug) => {
    if (!window.confirm("Réinitialiser ce template aux valeurs par défaut ?")) return;
    try {
      await api.delete(`/admin/email-templates/${slug}`);
      toast.success("Template réinitialisé");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const showPreview = async (slug) => {
    try {
      const r = await api.post(`/admin/email-templates/${slug}/preview`);
      setPreview({ slug, ...r.data });
    } catch { toast.error("Erreur d'aperçu"); }
  };

  return (
    <Section
      title="Templates emails automatiques"
      subtitle="Personnalisez l'objet et le corps des 7 emails envoyés automatiquement. Les variables {{ }} sont remplacées dynamiquement."
    >
      <div className="text-xs text-[#64748B] bg-[#F0FDFA] border border-[#A7F3D0] rounded-lg p-2.5 flex items-start gap-2" data-testid="email-tpl-help">
        <Info size={14} className="text-[#06B6D4] mt-0.5 flex-shrink-0" />
        <span>
          Cliquez sur un template pour l'éditer. <b>{`{{client_name}}`}</b>, <b>{`{{ref}}`}</b>, etc. sont des variables remplacées au moment de l'envoi.
          Laissez un champ identique au défaut pour utiliser le texte BESSON original.
        </span>
      </div>

      <div className="space-y-2" data-testid="email-tpl-list">
        {templates.map((t) => {
          const isOpen = openSlug === t.slug;
          const cur = draft[t.slug] || {};
          const hasChanges = (
            cur.subject !== t.subject ||
            cur.intro_html !== t.intro_html ||
            (cur.cta_label || "") !== (t.cta_label || "")
          );
          return (
            <div key={t.slug} className="border border-black/10 rounded-xl overflow-hidden" data-testid={`email-tpl-${t.slug}`}>
              <button
                type="button"
                onClick={() => setOpenSlug(isOpen ? null : t.slug)}
                className="w-full flex items-center justify-between gap-3 p-3 bg-white hover:bg-[#FAFAFA] transition-colors text-left"
                data-testid={`email-tpl-toggle-${t.slug}`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Mail size={16} className="text-[#06B6D4] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="display font-bold text-[#0A1128] text-sm truncate">{t.name}</div>
                    <div className="text-[11px] text-[#64748B] truncate">{t.subject}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {t.is_overridden && <span className="inline-block text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FFDCA8] text-[#92400E]">Personnalisé</span>}
                  {isOpen ? <ChevronDown size={16} className="text-[#64748B]" /> : <ChevronRight size={16} className="text-[#64748B]" />}
                </div>
              </button>

              {isOpen && (
                <div className="p-4 border-t border-black/10 bg-[#FAFAFA] space-y-3" data-testid={`email-tpl-editor-${t.slug}`}>
                  <SubjectField
                    slug={t.slug}
                    value={cur.subject || ""}
                    onChange={(v) => setField(t.slug, "subject", v)}
                    placeholder={t.default_subject}
                    variables={parseVariableTokens(t.variables)}
                  />
                  <div>
                    <Label className="text-xs">Corps du message</Label>
                    <EmailBodyEditor
                      value={cur.intro_html || ""}
                      onChange={(v) => setField(t.slug, "intro_html", v)}
                      variables={parseVariableTokens(t.variables)}
                      placeholder="Rédigez le corps de l'email…"
                      testId={`email-tpl-body-${t.slug}`}
                    />
                  </div>
                  {t.default_cta_label !== undefined && (
                    <div>
                      <Label className="text-xs">Texte du bouton CTA <span className="text-[#64748B]">(laissez vide pour masquer le bouton)</span></Label>
                      <Input
                        value={cur.cta_label || ""}
                        onChange={(e) => setField(t.slug, "cta_label", e.target.value)}
                        data-testid={`email-tpl-cta-${t.slug}`}
                        placeholder={t.default_cta_label}
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <Button
                      onClick={() => save(t.slug)}
                      disabled={savingSlug === t.slug || !hasChanges}
                      data-testid={`email-tpl-save-${t.slug}`}
                      className="bg-[#0A1128] text-white hover:bg-[#14213d] disabled:opacity-60"
                      size="sm"
                    >
                      {savingSlug === t.slug ? <><Loader2 size={12} className="animate-spin mr-1" />Enregistrement...</> : "Enregistrer"}
                    </Button>
                    <Button
                      onClick={() => showPreview(t.slug)}
                      variant="outline"
                      size="sm"
                      data-testid={`email-tpl-preview-${t.slug}`}
                    >
                      <Eye size={12} className="mr-1" />Aperçu
                    </Button>
                    {t.is_overridden && (
                      <Button
                        onClick={() => reset(t.slug)}
                        variant="outline"
                        size="sm"
                        data-testid={`email-tpl-reset-${t.slug}`}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <RefreshCcw size={12} className="mr-1" />Réinitialiser
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Preview modal */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPreview(null)}
          data-testid="email-tpl-preview-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-black/8 bg-[#FAFAFA] flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#06B6D4] font-bold">Aperçu email</div>
                <div className="display font-bold text-[#0A1128] truncate">{preview.subject}</div>
              </div>
              <button onClick={() => setPreview(null)} className="p-1.5 rounded-lg hover:bg-black/5 text-[#0A1128]" data-testid="email-tpl-preview-close">✕</button>
            </div>
            <div className="p-4 overflow-y-auto bg-[#FAFAFA]">
              <div className="bg-white rounded-xl p-2" dangerouslySetInnerHTML={{ __html: preview.html }} />
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

function PublicVisibilitySection() {
  const [busy, setBusy] = useState(false);
  const [flags, setFlags] = useState({ show_testimonials: false, show_google_reviews: false });

  useEffect(() => {
    api.get("/admin/settings").then((r) => setFlags({
      show_testimonials: !!r.data.show_testimonials,
      show_google_reviews: !!r.data.show_google_reviews,
    })).catch(() => {});
  }, []);

  const toggle = async (key) => {
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next);
    setBusy(true);
    try {
      await api.put("/admin/settings", { [key]: next[key] });
      toast.success(`${key === "show_testimonials" ? "Témoignages" : "Avis Google"} ${next[key] ? "affichés" : "masqués"} sur le site`);
    } catch {
      toast.error("Erreur — annulation");
      setFlags(flags);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      title="Visibilité publique — Avis & témoignages"
      subtitle="Activez chaque section quand vous avez collecté assez d'avis. Masqué par défaut."
    >
      <div className="space-y-3" data-testid="public-visibility">
        <Toggle
          testId="toggle-testimonials"
          label="Témoignages clients"
          desc="Section avec citations + vidéos sur la page d'accueil."
          checked={flags.show_testimonials}
          onChange={() => toggle("show_testimonials")}
          disabled={busy}
        />
        <Toggle
          testId="toggle-google-reviews"
          label="Avis Google"
          desc="Avis importés depuis votre fiche Google Business (source = google)."
          checked={flags.show_google_reviews}
          onChange={() => toggle("show_google_reviews")}
          disabled={busy}
        />
        <div className="text-xs text-[#64748B] bg-[#F0FDFA] border border-[#A7F3D0] rounded-lg p-2.5 flex items-start gap-2">
          <Info size={14} className="text-[#06B6D4] mt-0.5 flex-shrink-0" />
          <span>Quand les deux toggles sont désactivés, toute la section "Avis & témoignages" disparaît de la page d'accueil.</span>
        </div>
      </div>
    </Section>
  );
}

function Toggle({ label, desc, checked, onChange, disabled, testId }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-xl border ${checked ? "bg-[#F0FDFA] border-[#A7F3D0]" : "bg-white border-black/10"} cursor-pointer transition-colors ${disabled ? "opacity-60" : ""}`}>
      <button
        type="button"
        onClick={onChange}
        disabled={disabled}
        data-testid={testId}
        role="switch"
        aria-checked={checked}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${checked ? "bg-[#06B6D4]" : "bg-slate-300"}`}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="display font-bold text-[#0A1128] text-sm">{label}</div>
        <div className="text-xs text-[#64748B] mt-0.5">{desc}</div>
      </div>
      <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full ${checked ? "bg-[#06B6D4] text-white" : "bg-slate-200 text-slate-600"}`}>
        {checked ? "ON" : "OFF"}
      </span>
    </label>
  );
}

